import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  lazy,
  Suspense,
} from 'react';
import {
  Theme,
  seismicTheme,
  ToastsContainer,
  ToastsContext,
  IconFileAdd,
} from '@seismic/mantle';
import cn from 'classnames';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import useColors from 'ui/hooks/useColors';
import { routes } from 'constants/config';
import request from 'utils/request_ajax';
import { UploaderActions } from 'components/shared/UniversalUploader/UploaderActions';
import { UploaderLoader } from 'components/shared/UniversalUploader/UploaderLoader';
import ImageLibraryPicker from 'components/shared/ImageLibraryPicker';
import { useToggle } from 'hooks/index';
import withBrowserServices from 'utils/withBrowserServices';
import { File } from './File';
import {
  ERROR_MESSAGES,
  SERVER_GENERIC_ERROR,
  UPLOAD_OPTIONS,
  UPLOAD_STATUS,
} from './constants';
import { bytesToSize, fileInformationRequest } from './helpers';
import { HelperText } from './HelperText';

const SeismicUniversalUploader = lazy(() =>
  import(
    /* webpackChunkName: "UUC2" */ '@seismic/universal-upload-component'
  ).then(module => ({
    default: module.UniversalUploader,
  })),
);

const SeismicUniversalUploaderDnD = lazy(() =>
  import(
    /* webpackChunkName: "UUCDnd" */ '@seismic/universal-upload-component'
  ).then(module => ({
    default: module.UniversalUploaderDnd,
  })),
);

/**
  @typedef {object} Props
  @prop {string} uploaderFileId - Unique ID for the upload
  @prop {string} uploaderFileUrl - File URL
  @prop {array} accept - File types to accept
  @prop {function} onComplete - Callback function when upload is complete. Must return a promise
  @prop {function} onDelete - Callback function when file is going to be deleted. Must return a promise
  @prop {function} onFileError - Callback function when upload fails
  @prop {boolean} readOnly - Determine if user will be able to upload/delete a file
  @prop {boolean} showImageLibrary - Determine if user will be able to access the image library
  @prop {string} helperText - Helper text to display
  @prop {boolean} deleteUploadRecord - Determine if file will be (hard) deleted from the server
  @prop {number} maxSize - Maximum file size
  @prop {function} setDisableDismiss - Function to disable the modal's dismiss button
**/
const Uploader = withBrowserServices(
  ({
    uploaderFileId,
    accept,
    onComplete,
    onDelete,
    onFileError,
    readOnly = false,
    showImageLibrary = false,
    helperText,
    deleteUploadRecord = true,
    maxSize = 50 * 1024 * 1024,
    setDisableDismiss,
  }) => {
    const { colorPrimary } = useColors();
    const [fileName, setFileName] = useState(null);
    const [fileSize, setFileSize] = useState(null);
    const [currentProgress, setCurrentProgress] = useState(0);
    const [fileUrl, setFileUrl] = useState(null);
    const [uploadStatus, setUploadStatus] = useState(UPLOAD_STATUS.INITIAL);
    const [bssUploadId, setBssUploadId] = useState(uploaderFileId);
    const [isReadOnly, setIsReadOnly] = useState(readOnly);
    const [loading, setLoading] = useState(!!uploaderFileId);
    const [modalVisible, toggleModalVisible] = useToggle();
    const [errorMessage, setErrorMessage] = useState(null);
    const uucRef = useRef(null);
    const timer = useRef(null);

    // clear the interval when the component unmounts
    useEffect(() => {
      return () => {
        if (timer.current) clearInterval(timer.current);
      };
    }, []);

    const progressBarIncrement = () => {
      timer.current = setInterval(() => {
        setCurrentProgress(prev => {
          // we stay at 98% in the event that the progress bar gets close to 100, but we have not gotten a response from the API yet
          if (prev === 98 || prev === 100) {
            clearInterval(timer.current);
          }
          if (prev < 100) {
            return prev + 1;
          }
          return prev;
        });
      }, 1000);
    };

    const resetFile = () => {
      setFileName(null);
      setFileSize(null);
      setCurrentProgress(0);
      setFileUrl(null);
      setBssUploadId('');
      setIsReadOnly(false);
      setUploadStatus(UPLOAD_STATUS.INITIAL);
      setDisableDismiss(false);
      setErrorMessage(null);
    };

    const setFileDetails = useCallback(
      (name, size) => {
        if (!fileName) setFileName(name);
        if (!fileSize) setFileSize(bytesToSize(size));
      },
      [fileName, fileSize],
    );

    const handleOnFileError = useCallback(
      (
        file,
        error = new Error(SERVER_GENERIC_ERROR),
        newErrorMessage = ERROR_MESSAGES.SERVER_GENERIC,
      ) => {
        const { name, size } = file;
        if (timer.current) clearInterval(timer.current);
        setFileDetails(name, size);
        setUploadStatus(UPLOAD_STATUS.ERROR);
        setDisableDismiss(false);
        setErrorMessage(newErrorMessage);
        console.error(error, file);
        if (typeof onFileError === 'function') {
          onFileError(file, error);
        }
      },
      [onFileError, setFileDetails, setUploadStatus, setDisableDismiss],
    );

    const onFileSelectError = uucError => {
      if (readOnly) return;
      const { code, invalidFiles } = uucError;
      const file = invalidFiles[0];

      if (code === 'INVALID_FILE_EXTENSION') {
        handleOnFileError(
          file,
          new Error('Invalid file type'),
          ERROR_MESSAGES.FILE_TYPE,
        );
      } else {
        handleOnFileError(
          file,
          new Error(SERVER_GENERIC_ERROR),
          ERROR_MESSAGES.SERVER_GENERIC,
        );
      }
    };

    function completeFileUpload() {
      if (timer.current) clearInterval(timer.current);
      setCurrentProgress(100);
      setUploadStatus(UPLOAD_STATUS.COMPLETE);
    }

    useEffect(() => {
      const getFileDetails = async () => {
        if (!bssUploadId) return;
        setUploadStatus(UPLOAD_STATUS.UPLOADING);
        setDisableDismiss(true);
        try {
          const file = await fileInformationRequest(bssUploadId);
          const { name, size, url } = file;
          if (!name || !size) {
            handleOnFileError(
              file,
              new SyntaxError('File name or size is missing'),
            );
            return;
          }
          setFileName(name);
          setFileSize(bytesToSize(size));
          if (!url) {
            handleOnFileError(file, new SyntaxError('File url is missing'));
            return;
          }
          setFileUrl(url);
          setCurrentProgress(100);
          setUploadStatus(UPLOAD_STATUS.COMPLETE);
        } catch (e) {
          handleOnFileError({}, e);
        }
      };
      if (uploadStatus !== UPLOAD_STATUS.INITIAL) return;
      getFileDetails().then(() => setLoading(false));
    }, [bssUploadId, handleOnFileError, uploadStatus, setDisableDismiss]);

    const handleRemoveFile = async () => {
      setIsReadOnly(true);
      // if the file is less than 50% uploaded, we have not uploaded the file to the server yet, so we just remove it
      if (currentProgress < 50) {
        resetFile();
        return;
      }
      try {
        if (typeof onDelete === 'function') await onDelete();
        // We sometimes do not want to delete the file from the server (i.e. image element - since we can clone the element, clone the section, clone the lesson)
        if (deleteUploadRecord) {
          await request.delete(
            routes.FILE_UPLOAD.BSS.FILE_INFORMATION(bssUploadId),
          );
        }
      } catch (e) {
        console.error(e);
      } finally {
        resetFile();
        setIsReadOnly(false);
      }
    };

    const getSignedUrl = async uploadId => {
      const { url } = await fileInformationRequest(uploadId);
      if (!url) throw new SyntaxError('Missing signed URL');
      return url;
    };

    // todo - Future implementation of VFS (if needed)
    const vfsRequestManager = async newUpload => {
      console.warn(`${newUpload.file.name}: VFS functionality not implemented`);
    };

    const bssRequestManager = async newUpload => {
      const {
        file: { name, size },
        storageId,
        containerId,
      } = newUpload;
      try {
        const { bssUploadId: uploadId } = await request.postJSON(
          routes.FILE_UPLOAD.BSS.CREATE,
          {
            name,
            size,
            storageId,
            containerId,
          },
        );
        if (!uploadId) {
          handleOnFileError(newUpload, new SyntaxError('Missing upload ID'));
          return;
        }
        setBssUploadId(uploadId);
        const signedUrl = await getSignedUrl(uploadId);
        if (!signedUrl) {
          handleOnFileError(newUpload, new SyntaxError('Missing signed URL'));
          return;
        }
        setFileUrl(signedUrl);
        await onComplete({ ...newUpload, bssUploadId: uploadId });
        completeFileUpload();
      } catch (e) {
        handleOnFileError(newUpload, e);
      }
    };

    const handleComplete = async newUploads => {
      if (newUploads.length === 0) return;
      const newUpload = newUploads[0];

      progressBarIncrement();
      let requestManager;

      const { storageService } = newUpload;
      switch (storageService) {
        case 'bss':
          requestManager = bssRequestManager;
          break;
        case 'vfs':
          requestManager = vfsRequestManager;
          break;
        default:
          handleOnFileError(
            newUpload,
            new Error('Unknown source used to upload file'),
          );
          return;
      }
      await requestManager(newUpload);
    };

    const imageLibraryFileSelected = async selectedFile => {
      toggleModalVisible();
      const {
        bss_upload_id: fileBssUploadId,
        bss_signed_url: fileBssSignedUrl,
        file_name: name,
        file_size: size,
      } = selectedFile;
      setFileDetails(name, size);
      setBssUploadId(fileBssUploadId);
      setFileUrl(fileBssSignedUrl);
      setCurrentProgress(50);
      setUploadStatus(UPLOAD_STATUS.UPLOADING);
      progressBarIncrement();
      try {
        await onComplete(selectedFile, bssUploadId);
        completeFileUpload();
      } catch (e) {
        handleOnFileError({ name: fileName, size: fileSize }, e);
      }
    };

    const onFileProgress = (file, fileBytesSent, fileBytesTotal) => {
      // divide the progress by 2 since we're only using 50% of the progress bar for bss uploads
      setCurrentProgress(fileBytesSent / fileBytesTotal / 2);
    };

    const handleOnFileSelected = files => {
      if (uploadStatus !== UPLOAD_STATUS.INITIAL || readOnly) return;
      const { name, size } = files[0];
      setFileDetails(name, size);
      setUploadStatus(UPLOAD_STATUS.UPLOADING);
      setDisableDismiss(true);
    };

    const validate = async files => {
      if (uploadStatus !== UPLOAD_STATUS.INITIAL || readOnly) {
        return false;
      }

      const file = files[0];
      if (file.size > maxSize) {
        const maxFileSize = bytesToSize(maxSize);
        handleOnFileError(
          file,
          new Error(`File size exceeds the maximum size of ${maxFileSize}`),
          ERROR_MESSAGES.MAX_FILE_SIZE(maxFileSize),
        );
        return false;
      }

      return true;
    };

    if (loading) {
      return <UploaderLoader />;
    }

    return (
      <Suspense fallback={<UploaderLoader />}>
        <Theme
          theme={seismicTheme({
            useGlobal: true,
            customization: {
              colors: {
                primary: colorPrimary,
              },
            },
          })}
          className="w-100p"
        >
          <ToastsContext>
            <ToastsContainer />
            <div aria-label={t('universal_uploader.file_upload')}>
              <SeismicUniversalUploader
                uucRef={uucRef}
                className={cn('seismic-learning-universal-uploader', {
                  'seismic-learning-universal-uploader--error':
                    uploadStatus === UPLOAD_STATUS.ERROR,
                })}
                uploadMaxItems={1}
                accept={accept}
                bssUploadOptions={UPLOAD_OPTIONS}
                onComplete={handleComplete}
                showProgressModal={false}
                onFilesSelected={handleOnFileSelected}
                onFileProgress={onFileProgress}
                onFileError={handleOnFileError}
                onError={onFileSelectError}
                showToastOnError={false}
                validate={validate}
              >
                <SeismicUniversalUploaderDnD className="seismic-learning-universal-uploader--dnd">
                  {uploadStatus === UPLOAD_STATUS.INITIAL && (
                    <div className="ta-c">
                      <IconFileAdd color="#535B64" size="48" />
                      <Text
                        className="custom-dnd--text"
                        variant={TEXT_VARIANTS.MANTLE_BODY}
                        component="div"
                      >
                        {t('universal_uploader.drag_drop')}
                        <br />
                        {t('common.or')}
                      </Text>
                      <UploaderActions
                        uploadStatus={uploadStatus}
                        readOnly={readOnly}
                        showImageLibrary={showImageLibrary}
                        onLibraryButtonClick={toggleModalVisible}
                      />
                      <HelperText
                        additionalText={helperText}
                        maxSize={maxSize}
                      />
                    </div>
                  )}
                  {uploadStatus !== UPLOAD_STATUS.INITIAL && (
                    <File
                      fileName={fileName}
                      fileSize={fileSize}
                      fileUrl={fileUrl}
                      currentProgress={currentProgress}
                      handleRemoveFile={handleRemoveFile}
                      readOnly={isReadOnly}
                      uploadStatus={uploadStatus}
                      error={errorMessage}
                    />
                  )}
                </SeismicUniversalUploaderDnD>
              </SeismicUniversalUploader>
              {modalVisible && (
                <ImageLibraryPicker
                  onSelect={imageLibraryFileSelected}
                  onDismiss={toggleModalVisible}
                />
              )}
            </div>
          </ToastsContext>
        </Theme>
      </Suspense>
    );
  },
);

export default Uploader;
